<template>
	<div class="w-100">
		<div :class="classes">
			<label class="v-label v-label--active theme--light d-block mb-2 mt-n1">
				{{ label }}
			</label>

			<div
					v-if="editor"
					class="tiptap-input__toolbar"
					:class="{
						'flex-wrap': $vuetify.breakpoint.smAndDown,
						'tiptap-input__toolbar--has-focus': !!hasFocus
					}"
			>
				<template v-for="button in buttons">
					<component
							:is="button"
							class="mx-1"
							:editor="editor"
							:hasError="!!hasError"
							:hasFocus="!!hasFocus"
					/>
				</template>
			</div>

			<editor-content :editor="editor"/>
		</div>

		<v-input
				v-model="value"
				:hide-details="!counter ? 'auto' : false"
				:hint="hint"
				:persistentHint="persistentHint"
				:rules="localRules"
				type="hidden"
		>
			<template v-slot:message="slotProps">
				<!-- Display validation messages -->
				{{ slotProps.message }}
			</template>
			<!-- Display character counter -->
			<div
					v-if="computedCounter"
					class="v-counter theme--light"
					:class="{
					'error--text': getCharacterCount(value) > computedCounter
				}"
			>
				{{ getCharacterCount(value) }} / {{ computedCounter }}
			</div>
		</v-input>
	</div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
// Provides the base extensions 
import StarterKit from '@tiptap/starter-kit';
// Not part of the StarterKit
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';

import { helpers } from '../../helpers';

import Controls from './controls/index';

const possibleExtensions = [
	'Bold',
	'BulletList',
	'Code',
	'CodeBlock',
	'Divider',
	'Italic',
	'Link',
	'OrderedList',
	'Redo',
	'Underline',
	'Undo'
];

const defaultExtensions = [
	'Bold',
	'Underline',
	'Italic',
	'Divider',
	'Link',
	'Divider',
	'BulletList',
	'OrderedList',
	'Divider',
	'Divider',
	'Undo',
	'Redo'
];

export default {
	name: 'WysiwygInput',
	components: {
		EditorContent
	},
	props: {
		className: {
			type: String,
			default: ''
		},
		counter: [Boolean, Number, String],
		disabled: [Boolean, Number, String],
		extensions: {
			type: Array,
			default: () => (defaultExtensions),
			validator(value) {
				// Check for invalid extensions
				const invalidExtensions = value.filter(ext => !possibleExtensions.includes(ext));
				if(invalidExtensions.length > 0) {
					console.error(`Invalid extensions provided: ${invalidExtensions.join(', ')}`);
				}
				
				return true;
			}
		},
		label: String,
		hint: {
			type: String,
			default: ''
		},
		persistentHint: Boolean,
		rules: {
			type: Array,
			default: () => []
		},
		value: {
			type: String,
			default: '',
		}
	},
	data() {
		return {
			editor: null,
			hasFocus: false,
			markErrors: false
		}
	},

	mounted() {
		const vm = this;

		this.editor = new Editor({
			content: this.value,
			extensions: [
				StarterKit,
				Link.configure({
					openOnClick: false,
					defaultProtocol: 'https',
				}),
				Underline
			],

			onBlur({ editor, event }) {
				vm.markErrors = true;
				vm.hasFocus = false;
			},

			onFocus({ editor, event }) {
				vm.hasFocus = true;
			},


			onUpdate: () => {
				this.$emit('input', this.editor.getHTML());
			},
		});
	},

	computed: {
		classes() {
			const classes = ['w-100 tiptap-input__wrapper'];

			if(this.className)
				classes.push(this.className);

			if(this.getCharacterCount(this.value) < 1)
				classes.push('tiptap-input__wrapper--is-empty');

			if(this.counter)
				classes.push('tiptap-input__wrapper--has-counter');

			if(this.computedDisabled)
				classes.push('tiptap-input__wrapper--is-disabled');

			if(this.hasFocus)
				classes.push('tiptap-input__wrapper--has-focus');

			if(this.hasError)
				classes.push('tiptap-input__wrapper--has-error');

			return classes.join(' ');
		},

		buttons() {
			return this.extensions.map(button => Controls[button]);
		},

		computedCounter() {
			// If `counter` is falsy, return null
			if(!this.counter) {
				return null;
			}

			// If `counter` is a boolean and true, return default value 25
			if(typeof this.counter === 'boolean' && this.counter) {
				return 25;
			}

			// If `counter` is a string, attempt to convert it to a number
			if(typeof this.counter === 'string') {
				const parsed = Number(this.counter);
				return isNaN(parsed) ? null : parsed;
			}

			// If `counter` is already a number, return it
			if(typeof this.counter === 'number') {
				return this.counter;
			}

			// Fallback return null if none of the above matches
			return null;
		},

		computedDisabled() {
			// Coerce different types to a boolean
			if(typeof this.disabled === 'boolean') {
				return this.disabled;
			}

			if(typeof this.disabled === 'number') {
				return this.disabled !== 0; // Numbers other than 0 are truthy
			}

			if(typeof this.disabled === 'string') {
				return this.disabled !== '0'; // Any string other than '0' is truthy
			}

			// Fallback to false if none of the above cases match
			return false;
		},

		hasError() {
			return this.errorMessages.length && this.markErrors;
		},

		errorMessages() {
			const messages = [];

			if(this.editor) {
				for (const rule of this.localRules) {
					const result = rule(this.editor.getHTML());
					if(result !== true) {
						messages.push(result);
					}
				}
			}

			return messages;
		},

		localRules() {
			return [
				...this.rules,
				this.computedCounter
						? v => this.getCharacterCount(v) <= this.computedCounter || `Content must be ${this.computedCounter} characters or less.`
						: null
			].filter(Boolean);
		}
	},
	methods: {
		getCharacterCount(htmlContent) {
			return helpers.getCharacterCount(htmlContent);
		}
	},

	beforeDestroy() {
		this.editor.destroy()
	},

	watch: {
		value(value) {
			const isSame = this.editor.getHTML() === value;

			if(isSame) return;

			this.editor.commands.setContent(value, false);
		},
		computedDisabled(value) {
			this.editor.setEditable(!value);
		}
	}
}
</script>

<style lang="scss" scoped>
// Remove grey background from base Tiptap disabled style
::v-deep .tiptap-vuetify-editor__content--disabled:after {
	background: none !important;
}

// Style our wrapper to act just like a regular input
.tiptap-input {
	&__wrapper {
		background: white;
		border: solid 1px rgba(0, 0, 0, 0.38);
		border-radius: 0.5rem;
		padding: 0.5rem 0.75rem 0.75rem;
		position: relative;
		z-index: 1;

		// Disabled state
		&--is-disabled {
			border: solid 1px rgba(0, 0, 0, 0.26);
		}

		&::after {
			content: '';
			border: solid 0 currentColor !important;
			height: calc(100% + 2px);
			width: calc(100% + 2px);
			top: -1px;
			position: absolute;
			left: -1px;
			border-radius: 8px;
			opacity: 0;
			pointer-events: none !important;
			transition: all 300ms;
		}

		// Label default margin
		.v-label {
			margin: 0.75rem 0 0.75rem !important; // new
		}

		// Focus state
		&--has-focus {
			border: solid 2px var(--v-primary-base);
			// padding: 0.3125rem 0.6875rem 0.9325rem;
			padding: 0.4375rem 0.6875rem 0.6875rem;

			&::after {
				height: calc(100% + 12px);
				width: calc(100% + 12px);
				top: -6px;
				left: -6px;
				border-radius: 12px;
				border: solid 5px var(--v-primary-base) !important;
				opacity: 0.2;
			}

			.v-label {
				color: var(--v-primary-base) !important;
				margin: 0.5rem 0 0.5rem !important;
			}

			.tiptap-input__toolbar {
				background-color: #f3f7e9 !important;
			}
		}

		// Error state
		&--has-error {
			border: solid 2px var(--v-error-base);
			padding: 0.4375rem 0.6875rem 0.6875rem;

			&::after {
				height: calc(100% + 12px);
				width: calc(100% + 12px);
				top: -6px;
				left: -6px;
				border-radius: 12px;
				border: solid 5px var(--v-error-base) !important;
				opacity: 0.2;
			}

			.v-label {
				color: var(--v-error-base) !important;
			}

			.tiptap-input__toolbar {
				background-color: #fbf3f3 !important;
			}
		}

		// Apply label size for both focused and not-empty states
		&--has-focus,
		&:not(&--has-focus):not(&--is-empty) {
			.v-label {
				font-size: 0.75rem;
				margin: 0.5rem 0 0.5rem !important;
			}
		}

		::v-deep .ProseMirror {
			padding: 0.25rem 0;
		}

	}

	&__toolbar {
		background-color: rgba(0, 0, 0, 0.04) !important;
		border-radius: 0.5rem !important;
		display: flex;
		padding: 0.5rem 0.375rem;
		position: relative;
		margin-bottom: 0.75rem;

		& .v-btn {
			opacity: 0.5;
			transition-duration: 0.28s;
			transition-property: background-color, box-shadow, color, transform, opacity;
			transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

			&::v-deep i {
				font-size: 1.25rem !important;
			}
		}

		&--has-focus .v-btn {
			opacity: 1;
		}
	}
}

// When counter is present
::v-deep .v-messages {
	padding-left: 0.75rem;
	width: calc(100% - 5.75rem);
}

.v-counter {
	position: absolute;
	bottom: -1.25rem;
	right: 0.75rem;
}

::v-deep .ProseMirror {
	outline: none !important;
}
</style>
